import { MultiSelectOption } from 'components/ui/MultiSelect';
import {
  APP_OFFLINE_AFTER_SILENCE_SECONDS,
  FOCUS_PERCENT_BOUNDS,
  EMPTY_COLUMN_PLACEHOLDER
} from 'constants/class-management.constant';
import { LOCAL_STORAGE } from 'constants/general';
import { getLocalStorage, updateLocalStorage } from 'utils/helpers/storage';
import {
  SECONDS_PER_MINUTE,
  TIME_UNIT_CONVERT_FACTOR
} from 'constants/general';
import {
  CLASS_IN_FOCUS_DISPLAY,
  CLASS_MEMBER_ROLE,
  CLASS_PARTICIPANT_STATUS,
  CLASS_SESSION_STATUS,
  TEAM_ACCESS_ROLE,
  TEAM_MEMBER_ROLE
} from 'enums';
import {
  AmendSessionTaskInput,
  ClassCandidateMember,
  ClassFocusSession,
  ClassMemberDisplayFields,
  ClassParticipantUpdatedEvent,
  ClassSessionParticipant,
  ClassSessionTask,
  ClassStudentDisplayFields,
  ClassTaskProgressFields,
  ClassUnlockRequest,
  ClassInfo,
  ClassSessionListItem,
  LiveSessionTasksValidation,
  SessionTaskDraft,
  StartClassSessionTaskInput
} from 'interfaces/classes.interface';
import { Team, TeamMember, TeamsMembers } from 'interfaces/team.interface';
import { t } from 'services/i18n.service';
import { getMaxBy } from 'utils/array.util';
import { clampRounded, formatPercent, ratioToPercent } from 'utils/number.util';
import { maskEmail } from 'utils/team.util';

/**
 * FE fallback: `GET team-management/all-members` can return the same
 * admin id twice (owner + team_to_admin). Dedupe until the API is fixed.
 */
export const dedupeClassRoster = ({
  members,
  admins,
  teachers
}: TeamsMembers): TeamsMembers => ({
  members: members.filter(
    (member, index, list) =>
      list.findIndex((entry) => entry.id === member.id) === index
  ),
  admins: [...new Set(admins)],
  teachers: [...new Set(teachers)]
});

/** Split roster members by `teachers[]` ids — once after dedupe, stored in Redux. */
export const buildClassRosterPartitions = (
  roster: TeamsMembers
): { classStudents: TeamMember[]; classTeachers: TeamMember[] } => {
  const teacherIdSet = new Set(roster.teachers);
  const classStudents: TeamMember[] = [];
  const classTeachers: TeamMember[] = [];
  for (const member of roster.members) {
    if (member.id && teacherIdSet.has(member.id)) {
      classTeachers.push(member);
    } else {
      classStudents.push(member);
    }
  }
  return { classStudents, classTeachers };
};

export const clampFocusPercent = (value: number): number =>
  clampRounded(value, FOCUS_PERCENT_BOUNDS.MIN, FOCUS_PERCENT_BOUNDS.MAX);

export const getStudentDisplayName = (
  student: ClassStudentDisplayFields,
  options?: { shouldMaskEmail?: boolean }
): string => {
  if (student.first_name && student.last_name) {
    return `${student.first_name} ${student.last_name}`;
  }
  const email = student.email ?? undefined;
  const shouldMaskEmail = options?.shouldMaskEmail !== false;
  const emailLabel = shouldMaskEmail ? maskEmail(email) : email;
  return student.first_name || emailLabel || student.student_id;
};

/** Index the live roster by student id so records lacking a name can borrow one. */
export const buildStudentDisplayFieldsById = (
  students: ClassStudentDisplayFields[]
): Map<string, ClassStudentDisplayFields> =>
  new Map(students.map((student) => [student.student_id, student]));

/**
 * Display name for a record that may only carry a `student_id` (e.g. an unlock
 * request), enriched from the live roster when the record itself has no name.
 * Falls back to `getStudentDisplayName` so a UUID is only shown as a last resort.
 */
export const resolveStudentDisplayName = (
  student: ClassStudentDisplayFields,
  rosterById: Map<string, ClassStudentDisplayFields>
): string => {
  const rosterEntry = rosterById.get(student.student_id);
  return getStudentDisplayName(
    {
      student_id: student.student_id,
      first_name: student.first_name ?? rosterEntry?.first_name,
      last_name: student.last_name ?? rosterEntry?.last_name,
      email: student.email ?? rosterEntry?.email
    },
    { shouldMaskEmail: false }
  );
};

export const getUnlockRequestedItem = (request: ClassUnlockRequest): string =>
  request.requested_app ?? request.requested_domain ?? '';

/** Full name → email → empty string (roster/candidate rows show emails raw). */
export const getMemberDisplayName = (
  member: ClassMemberDisplayFields
): string => {
  const fullName = [member.first_name, member.last_name]
    .filter(Boolean)
    .join(' ');
  return fullName || member.email || '';
};

/**
 * Students-table Name column: real name only — email lives in its own column,
 * so missing names use the shared empty-cell dash.
 */
export const getMemberNameColumnLabel = (
  member: ClassMemberDisplayFields
): string => {
  const fullName = [member.first_name, member.last_name]
    .filter(Boolean)
    .join(' ');
  return fullName || EMPTY_COLUMN_PLACEHOLDER;
};

/** Locale-aware `93%` when known, an em dash when not. */
export const formatFocusPercentLabel = (
  percent: number | null | undefined
): string =>
  typeof percent === 'number' && !Number.isNaN(percent)
    ? formatPercent(clampFocusPercent(percent))
    : EMPTY_COLUMN_PLACEHOLDER;

export const formatSessionDuration = (durationSeconds: number): string => {
  const minutes = Math.round(durationSeconds / SECONDS_PER_MINUTE);
  const minutesPerHour = TIME_UNIT_CONVERT_FACTOR.ONE_HOUR_AS_MINUTES;
  const hours = Math.floor(minutes / minutesPerHour);
  return hours ? `${hours}h ${minutes % minutesPerHour}m` : `${minutes}m`;
};

const isSchoolStaffRole = (schoolRole: TEAM_MEMBER_ROLE): boolean =>
  schoolRole === TEAM_MEMBER_ROLE.ADMIN ||
  schoolRole === TEAM_MEMBER_ROLE.TEACHER;

/**
 * Who can be newly enrolled for this role — used by `useClassEnrolmentPicker`
 * to hide already-enrolled noise from the MultiSelect.
 * Add teacher: school admin/teacher not already teaching this class.
 * Add student: school student (member) not already in the class.
 */
export const isCandidateEnrolableForRole = (
  candidate: ClassCandidateMember,
  role: CLASS_MEMBER_ROLE
): boolean => {
  if (!candidate.is_registered || !candidate.user_id) {
    return false;
  }
  if (role === CLASS_MEMBER_ROLE.TEACHER) {
    return (
      isSchoolStaffRole(candidate.school_role) && !candidate.is_class_teacher
    );
  }
  return (
    candidate.school_role === TEAM_MEMBER_ROLE.MEMBER && !candidate.in_class
  );
};

export const filterEnrolableCandidates = (
  candidates: ClassCandidateMember[],
  role: CLASS_MEMBER_ROLE
): ClassCandidateMember[] =>
  candidates.filter((candidate) =>
    isCandidateEnrolableForRole(candidate, role)
  );

/** Clean MultiSelect option — name/email only, no enrolment-status suffix. */
export const buildEnrolableCandidateOption = (
  candidate: ClassCandidateMember
): MultiSelectOption => {
  const name = getMemberDisplayName(candidate);
  const label =
    candidate.email && name !== candidate.email
      ? `${name} (${candidate.email})`
      : name;
  return {
    value: candidate.user_id ?? candidate.member_id,
    label
  };
};

/**
 * Restricted (teacher) picker: show server hits only when the typed query has
 * settled; always keep prior selections visible so they can be unticked.
 */
export const buildRestrictedCandidateOptions = ({
  fetchedOptions,
  selectedOptions,
  shouldShowSearchResults
}: {
  fetchedOptions: MultiSelectOption[];
  selectedOptions: MultiSelectOption[];
  shouldShowSearchResults: boolean;
}): MultiSelectOption[] => {
  const visibleOptions = shouldShowSearchResults ? fetchedOptions : [];
  const retainedSelected = selectedOptions.filter(
    (selected) =>
      !visibleOptions.some(
        (option) => String(option.value) === String(selected.value)
      )
  );
  return [...retainedSelected, ...visibleOptions];
};

/** Empty-state copy for the restricted candidate MultiSelect. */
export const getRestrictedCandidateNoOptionsMessage = ({
  searchQuery,
  debouncedSearchQuery,
  isLoading,
  minQueryLength
}: {
  searchQuery: string;
  debouncedSearchQuery: string;
  isLoading: boolean;
  minQueryLength: number;
}): string | undefined => {
  const trimmedQuery = searchQuery.trim();
  if (trimmedQuery.length < minQueryLength) {
    return t('class_mode.search_students_hint', { count: minQueryLength });
  }
  const isQuerySynced = trimmedQuery === debouncedSearchQuery.trim();
  if (!isQuerySynced || isLoading) {
    return t('class_mode.searching_candidates');
  }
  return undefined;
};

export const countCandidateOptionsMatchingQuery = (
  options: MultiSelectOption[],
  query: string
): number => {
  const trimmedQuery = query.trim();
  if (trimmedQuery.length === 0) {
    return options.length;
  }
  const lowered = trimmedQuery.toLowerCase();
  return options.filter((option) =>
    option.label.toLowerCase().includes(lowered)
  ).length;
};

/** Admin/owner: remote `?q=` only when local filter has no hits. */
export const shouldFetchCandidateRemoteFallback = ({
  query,
  localMatchCount,
  minQueryLength
}: {
  query: string;
  localMatchCount: number;
  minQueryLength: number;
}): boolean => query.trim().length >= minQueryLength && localMatchCount === 0;

// Separate tickets so admin remote-fallback searches never cancel the browsable
// list load (and vice versa). Out-of-order responses still drop within each lane.
let latestCandidateListFetchId = 0;
let latestCandidateSearchFetchId = 0;

export const beginCandidateListFetch = (): number => {
  latestCandidateListFetchId += 1;
  return latestCandidateListFetchId;
};

export const beginCandidateSearchFetch = (): number => {
  latestCandidateSearchFetchId += 1;
  return latestCandidateSearchFetchId;
};

export const isCurrentCandidateListFetch = (fetchId: number): boolean =>
  fetchId === latestCandidateListFetchId;

export const isCurrentCandidateSearchFetch = (fetchId: number): boolean =>
  fetchId === latestCandidateSearchFetchId;

/** Bumps both lanes so in-flight responses cannot refill cleared restricted hits. */
export const invalidateCandidateMemberFetches = (): void => {
  latestCandidateListFetchId += 1;
  latestCandidateSearchFetchId += 1;
};

/**
 * Seconds since the participant was last heard from — their latest heartbeat,
 * or their join when no heartbeat has landed yet. Clamped at 0 so a client
 * clock running behind the server never shows a negative age.
 */
export const getHeartbeatSilenceSeconds = (
  participant: Pick<ClassSessionParticipant, 'last_heartbeat_at' | 'joined_at'>,
  nowMs: number
): number | null => {
  const lastSignalAt = participant.last_heartbeat_at ?? participant.joined_at;
  if (!lastSignalAt) {
    return null;
  }
  const silenceMs = nowMs - new Date(lastSignalAt).getTime();
  return Math.max(
    0,
    Math.floor(silenceMs / TIME_UNIT_CONVERT_FACTOR.ONE_SECOND_AS_MILLISECONDS)
  );
};

const resolveSilentDisplay = (
  silenceSeconds: number | null
): CLASS_IN_FOCUS_DISPLAY =>
  silenceSeconds !== null && silenceSeconds <= APP_OFFLINE_AFTER_SILENCE_SECONDS
    ? CLASS_IN_FOCUS_DISPLAY.CONNECTION_ISSUE
    : CLASS_IN_FOCUS_DISPLAY.APP_OFFLINE;

/**
 * Live "in focus now" display from join status + heartbeat fields.
 * `app_offline` is server-computed (heartbeat silence past the backend's
 * short gap window); it only escalates to "offline" once the silence passes
 * APP_OFFLINE_AFTER_SILENCE_SECONDS.
 */
export const resolveInFocusDisplay = (
  participant: ClassSessionParticipant,
  nowMs: number
): CLASS_IN_FOCUS_DISPLAY => {
  if (participant.join_status !== CLASS_PARTICIPANT_STATUS.JOINED) {
    return CLASS_IN_FOCUS_DISPLAY.DASH;
  }
  if (participant.app_offline) {
    return resolveSilentDisplay(getHeartbeatSilenceSeconds(participant, nowMs));
  }
  if (
    participant.currently_in_focus === null ||
    participant.currently_in_focus === undefined
  ) {
    return CLASS_IN_FOCUS_DISPLAY.NO_HEARTBEAT;
  }
  return participant.currently_in_focus
    ? CLASS_IN_FOCUS_DISPLAY.IN_FOCUS
    : CLASS_IN_FOCUS_DISPLAY.NOT_IN_FOCUS;
};

/**
 * Live `% of session time in focus` for a participant. Prefers the
 * server-computed `in_focus_pct`; falls back to a client-side estimate from
 * `in_focus_seconds` over the participant's joined window. Returns null when
 * the participant has not joined yet.
 */
export const getParticipantInFocusPercent = (
  participant: ClassSessionParticipant,
  session: ClassFocusSession
): number | null => {
  if (typeof participant.in_focus_pct === 'number') {
    return clampFocusPercent(participant.in_focus_pct);
  }

  if (!participant.joined_at) {
    return null;
  }

  const windowStart = Math.max(
    new Date(session.start_time).getTime(),
    new Date(participant.joined_at).getTime()
  );
  const windowEnd = session.ended_at
    ? new Date(session.ended_at).getTime()
    : Date.now();
  const windowSeconds = (windowEnd - windowStart) / 1000;

  if (windowSeconds <= 0) {
    return null;
  }

  return clampFocusPercent(
    ratioToPercent(participant.in_focus_seconds, windowSeconds)
  );
};

/**
 * Merges a `class-participant-updated` push into the matching roster row.
 * Only fields present on the payload are applied (older backends omit the
 * heartbeat-presence fields). An unknown student id is a no-op — the
 * presence listener follows up with a full roster fetch for those.
 */
export const mergeParticipantPresence = (
  participants: ClassSessionParticipant[],
  payload: ClassParticipantUpdatedEvent
): ClassSessionParticipant[] =>
  participants.map((participant) => {
    if (participant.student_id !== payload.student_id) {
      return participant;
    }
    return {
      ...participant,
      join_status: payload.join_status,
      // The backend sends the permission pair only once the student's app has
      // reported it, so an absent pair keeps the last known state.
      blocking_permissions_enabled:
        payload.blocking_permissions_enabled ??
        participant.blocking_permissions_enabled,
      missing_blocking_permissions:
        payload.missing_blocking_permissions ??
        participant.missing_blocking_permissions,
      currently_in_focus:
        payload.currently_in_focus ?? participant.currently_in_focus,
      in_focus_pct: payload.in_focus_pct ?? participant.in_focus_pct,
      app_offline: payload.app_offline ?? participant.app_offline,
      // `null` means "no heartbeat recorded" and must be applied — only an
      // absent key (older backend) keeps the previous value.
      last_heartbeat_at:
        payload.last_heartbeat_at === undefined
          ? participant.last_heartbeat_at
          : payload.last_heartbeat_at,
      // Tolerate payloads without task counts (older sessions / no tasks).
      tasks_completed: payload.tasks_completed ?? participant.tasks_completed,
      tasks_total: payload.tasks_total ?? participant.tasks_total
    };
  });

export const toCount = (value: number | null | undefined): number =>
  typeof value === 'number' && !Number.isNaN(value) ? value : 0;

/** Most recently ended session by `ended_at` (null when none have an end time). */
export const getMostRecentEndedSession = (
  sessions: ClassSessionListItem[]
): ClassSessionListItem | null =>
  getMaxBy(
    sessions.filter(
      (session): session is ClassSessionListItem & { ended_at: string } =>
        Boolean(session.ended_at)
    ),
    (session) => new Date(session.ended_at).getTime()
  );

/**
 * Idle "last session" card: prefer an already-loaded history list; otherwise
 * the just-ended `liveSession` left in Redux after end (no sessions GET).
 */
export const resolveIdleLastSession = ({
  sessions,
  liveSession
}: {
  sessions: ClassSessionListItem[];
  liveSession: ClassFocusSession | null;
}): ClassSessionListItem | null => {
  const fromList = getMostRecentEndedSession(sessions);
  if (fromList) {
    return fromList;
  }
  if (
    liveSession?.status === CLASS_SESSION_STATUS.ENDED &&
    liveSession.ended_at
  ) {
    return liveSession;
  }
  return null;
};

/**
 * Mean of ended sessions' `avg_in_focus_pct` that are numbers; null when none.
 */
export const getAverageSessionFocusPercent = (
  sessions: ClassSessionListItem[]
): number | null => {
  const focusValues = sessions
    .map((session) => session.avg_in_focus_pct)
    .filter((value): value is number => typeof value === 'number');
  if (focusValues.length === 0) {
    return null;
  }
  const total = focusValues.reduce((sum, value) => sum + value, 0);
  return total / focusValues.length;
};

/** Number of roster rows currently joined in a live session. */
export const countJoinedParticipants = (
  participants: Pick<ClassSessionParticipant, 'join_status'>[]
): number =>
  participants.filter(
    (participant) => participant.join_status === CLASS_PARTICIPANT_STATUS.JOINED
  ).length;

/** Updates overview card counts for the class whose live session matches. */
export const patchLiveJoinedCountOnClasses = (
  classes: ClassInfo[],
  {
    classSessionId,
    joinedCount
  }: {
    classSessionId: string;
    joinedCount: number;
  }
): ClassInfo[] =>
  classes.map((classInfo) => {
    if (classInfo.live_session?.id !== classSessionId) {
      return classInfo;
    }
    return {
      ...classInfo,
      live_joined_count: joinedCount
    };
  });

export const normalizeTaskTitle = (title: string): string =>
  title.trim().toLowerCase();

/** True when `title` matches an existing task row (trim + case-insensitive). */
export const isDuplicateTaskTitle = (
  tasks: Pick<SessionTaskDraft, 'title'>[],
  title: string
): boolean => {
  const normalized = normalizeTaskTitle(title);
  if (!normalized.length) {
    return false;
  }
  return tasks.some((task) => normalizeTaskTitle(task.title) === normalized);
};

export const getLiveSessionTasksValidation = (
  drafts: Pick<SessionTaskDraft, 'title'>[]
): LiveSessionTasksValidation => {
  const normalizedTitles = drafts.map((draft) =>
    normalizeTaskTitle(draft.title)
  );
  const filledTitles = normalizedTitles.filter((title) => title.length > 0);
  const hasBlankTitle = filledTitles.length < normalizedTitles.length;
  const hasDuplicateTitle = new Set(filledTitles).size < filledTitles.length;
  return {
    isValid: !hasBlankTitle && !hasDuplicateTitle,
    hasBlankTitle,
    hasDuplicateTitle
  };
};

/**
 * Maps the "Tasks for students" editor rows to the start-session DTO: trims
 * titles, drops empty rows, keeps a positive duration only, and assigns a
 * stable 0-based `order` from the (already reordered) row position.
 */
export const buildSessionTasksPayload = (
  drafts: SessionTaskDraft[]
): StartClassSessionTaskInput[] =>
  drafts
    .map((draft) => ({ ...draft, title: draft.title.trim() }))
    .filter((draft) => draft.title.length > 0)
    .map((draft, index) => ({
      title: draft.title,
      duration_minutes:
        typeof draft.duration_minutes === 'number' && draft.duration_minutes > 0
          ? draft.duration_minutes
          : undefined,
      order: index
    }));

export const mapClassSessionTasksToDrafts = (
  tasks: ClassSessionTask[] | undefined
): SessionTaskDraft[] =>
  (tasks ?? []).map((task) => ({
    id: task.id,
    title: task.title?.trim() ?? '',
    duration_minutes: task.duration_minutes ?? null
  }));

/** True when draft rows match baseline (order, id, trimmed title, duration). */
export const areSessionTaskDraftsEqual = (
  left: SessionTaskDraft[],
  right: SessionTaskDraft[]
): boolean => {
  if (left.length !== right.length) {
    return false;
  }
  return left.every((task, index) => {
    const other = right[index];
    return (
      (task.id ?? '') === (other.id ?? '') &&
      task.title.trim() === other.title.trim() &&
      (task.duration_minutes ?? null) === (other.duration_minutes ?? null)
    );
  });
};

export const buildAmendSessionTasksPayload = (
  drafts: SessionTaskDraft[]
): AmendSessionTaskInput[] =>
  drafts.map((draft, index) => ({
    id: draft.id ?? '',
    title: draft.title.trim(),
    duration_minutes:
      typeof draft.duration_minutes === 'number' && draft.duration_minutes > 0
        ? draft.duration_minutes
        : null,
    order: index
  }));

/** Whether the live session carries any student tasks (drives the roster column). */
export const sessionHasTasks = (
  session: Pick<ClassFocusSession, 'tasks'> | null | undefined
): boolean => (session?.tasks?.length ?? 0) > 0;

/**
 * Whether any report participant has assigned tasks. The report envelope has
 * no session `tasks`, so the column is inferred from participant totals.
 */
export const reportHasTasks = (
  participants: ClassTaskProgressFields[]
): boolean =>
  participants.some((participant) => toCount(participant.tasks_total) > 0);

/** `3/5` task-completion label, or null when the participant has no tasks. */
export const getTaskProgressLabel = (
  fields: ClassTaskProgressFields
): string | null => {
  const total = toCount(fields.tasks_total);
  if (total <= 0) {
    return null;
  }
  return `${toCount(fields.tasks_completed)}/${total}`;
};

export const getParentTeamName = (
  classInfo: ClassInfo,
  teams: Team[],
  selectedTeam?: Team | null
): string | null => {
  if (!classInfo.parent_team_id) {
    return null;
  }
  if (selectedTeam?.id === classInfo.parent_team_id && selectedTeam.name) {
    return selectedTeam.name;
  }
  return (
    teams.find((team) => team.id === classInfo.parent_team_id)?.name ?? null
  );
};

export const getLiveSessionChipText = (classInfo: ClassInfo): string => {
  const liveSession = classInfo.live_session;
  const isLive = liveSession?.status === CLASS_SESSION_STATUS.LIVE;
  if (!isLive) {
    return t('class_mode.no_live_session');
  }
  if (liveSession?.name) {
    return t('class_mode.live_session_named', { name: liveSession.name });
  }
  return t('class_mode.live_session_chip');
};

/** Teams that can be a class parent (excludes class rows until admin-teams is fixed). */
export const getEligibleParentTeams = (
  teams: Team[],
  classes: ClassInfo[]
): Team[] => {
  const classIds = new Set(classes.map((classInfo) => classInfo.id));
  return teams.filter(
    (team) => team.is_class !== true && !classIds.has(team.id)
  );
};

/**
 * Picks the parent team for create-class:
 * - one option → that team id
 * - current selection still in options → keep it
 * - otherwise → empty (user must choose)
 */
export const resolveParentTeamIdSelection = ({
  optionValues,
  currentParentTeamId
}: {
  optionValues: Array<string | number>;
  currentParentTeamId: string;
}): string => {
  if (optionValues.length === 1) {
    return String(optionValues[0]);
  }
  const isSelectionValid = optionValues.some(
    (value) => String(value) === currentParentTeamId
  );
  return isSelectionValid ? currentParentTeamId : '';
};

/**
 * Back target from a class detail page (Part H): school team screen where
 * classes are listed. Null when the class has no parent team.
 */
export const getClassBackPath = (
  parentTeamId?: string | null
): string | null => (parentTeamId ? `/team/${parentTeamId}` : null);

/**
 * True when the current user only has teacher access on the class's parent
 * school team (Part H — no full-roster browse).
 */
export const isTeacherAccessForClass = ({
  teams,
  parentTeamId
}: {
  teams: Array<Pick<Team, 'id' | 'access_role'>>;
  parentTeamId?: string | null;
}): boolean =>
  Boolean(parentTeamId) &&
  teams.some(
    (team) =>
      team.id === parentTeamId && team.access_role === TEAM_ACCESS_ROLE.TEACHER
  );

/**
 * True when the parent school is in the user's admin-teams list with admin
 * rights. Owners come back without `access_role`, so anything that isn't the
 * teacher role counts.
 */
export const isSchoolAdminForClass = ({
  teams,
  parentTeamId
}: {
  teams: Array<Pick<Team, 'id' | 'access_role'>>;
  parentTeamId?: string | null;
}): boolean =>
  Boolean(parentTeamId) &&
  teams.some(
    (team) =>
      team.id === parentTeamId && team.access_role !== TEAM_ACCESS_ROLE.TEACHER
  );

/** School admin on the parent team, or creator/owner of this class. */
export const canManageClassIdentity = ({
  currentUserId,
  selectedClass,
  teams
}: {
  currentUserId?: string | null;
  selectedClass: Pick<ClassInfo, 'owner_id' | 'parent_team_id'>;
  teams: Array<Pick<Team, 'id' | 'access_role'>>;
}): boolean => {
  const parentTeamId = selectedClass.parent_team_id;
  const isSchoolAdmin =
    Boolean(parentTeamId) &&
    teams.some(
      (team) =>
        team.id === parentTeamId && team.access_role === TEAM_ACCESS_ROLE.ADMIN
    );
  if (isSchoolAdmin) {
    return true;
  }
  return (
    Boolean(currentUserId) &&
    Boolean(selectedClass.owner_id) &&
    selectedClass.owner_id === currentUserId
  );
};

export const normalizeClassName = (name: string): string =>
  name.trim().toLowerCase();

/**
 * Resolve a class-rename draft into the value to submit, or `null` when there
 * is nothing to save (empty after trimming, or unchanged from the current
 * name). Keeps the rename component's submit/no-op decision testable.
 */
export const getClassRenameSubmission = (
  currentName: string | null,
  draft: string
): string | null => {
  const trimmed = draft.trim();
  if (!trimmed || trimmed === (currentName ?? '')) {
    return null;
  }
  return trimmed;
};

/** True when another class already uses this name under the same parent team. */
export const hasDuplicateClassNameUnderParent = ({
  classes,
  name,
  parentTeamId
}: {
  classes: ClassInfo[];
  name: string;
  parentTeamId: string;
}): boolean => {
  const normalizedName = normalizeClassName(name);
  if (!normalizedName || !parentTeamId) {
    return false;
  }
  return classes.some(
    (classInfo) =>
      classInfo.parent_team_id === parentTeamId &&
      normalizeClassName(classInfo.name ?? '') === normalizedName
  );
};

const lastSharedFocusModeKey = (classId: string): string =>
  `${LOCAL_STORAGE.CLASS_LAST_SHARED_FOCUS_MODE}-${classId}`;

/** The shared focus mode id last used to start a session for this class. */
export const getLastClassSharedFocusModeId = (classId: string): string | null =>
  getLocalStorage(lastSharedFocusModeKey(classId));

/** Remember the shared focus mode used to start a session for this class. */
export const saveLastClassSharedFocusModeId = (
  classId: string,
  focusModeId: string
): void => updateLocalStorage(lastSharedFocusModeKey(classId), focusModeId);
